import { useState } from "react";
import { toast } from "sonner";
import {
    Database,
    Copy,
    ArrowClockwise,
    Sparkle,
    ArrowSquareOut,
} from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "@/components/ui/dialog";
import { generateDataDrivenLanding, errText } from "@/lib/api";
import { copyToClipboard } from "@/lib/csv";

const LoadingState = () => (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
        <div className="h-10 w-10 border-2 border-ink border-t-transparent rounded-full animate-spin" />
        <p className="mono-label text-neutral-600">
            Writing copy from research + generating hero background · ~30s
        </p>
    </div>
);

// A single clearly-labelled plain-text block, ready to paste into any design tool.
const buildLovableBlock = (data) => {
    const c = data?.content || {};
    const lines = [
        `NICHE: ${data?.business_type || ""}`,
        `LOCATION: ${data?.city || ""}${data?.country_label ? `, ${data.country_label}` : ""}`,
        "",
        `HIGH-PRIORITY KEYWORDS (${(data?.priority_keywords || []).length}):`,
        ...(data?.priority_keywords || []).map((k) => `  - ${k}`),
        "",
        `HEADLINE:`,
        `  ${c.headline || ""}`,
        "",
        `SUB-HEADLINES:`,
        ...(c.sub_headlines || []).map((s, i) => `  ${i + 1}. ${s}`),
        "",
        `BODY COPY:`,
        ...(c.body_paragraphs || []).flatMap((p, i) => [`  Paragraph ${i + 1}:`, `  ${p}`, ""]),
        `CTA BUTTON: ${c.cta_text || ""}`,
        "",
        `HERO IMAGE BRIEF: ${c.hero_image_prompt || ""}`,
    ];
    return lines.join("\n");
};

export const DataDrivenLandingDialog = ({ result }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    const start = async () => {
        if (!result?.id) {
            toast.error("Search must be saved first");
            return;
        }
        setLoading(true);
        setError("");
        setData(null);
        try {
            const resp = await generateDataDrivenLanding(result.id);
            setData(resp);
        } catch (e) {
            const msg = errText(e, "Generation failed");
            setError(String(msg));
            toast.error(String(msg));
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLovable = async () => {
        if (!data) return;
        const block = buildLovableBlock(data);
        const ok = await copyToClipboard(block);
        if (ok) toast.success("Copied for Lovable — paste into your design tool");
        else toast.error("Could not copy — try again");
    };

    const heroSrc = data?.image_b64
        ? `data:${data.image_mime || "image/png"};base64,${data.image_b64}`
        : "";

    const c = data?.content || {};

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (o && !data && !loading) start();
            }}
        >
            <DialogTrigger asChild>
                <button
                    data-testid="ddl-btn"
                    className="brutal-btn bg-klein border border-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    title="Generate a research-driven landing page using top keywords + competitor gaps"
                >
                    <Database size={13} weight="bold" />
                    Landing Page from Research
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="ddl-dialog"
                className="max-w-6xl w-[95vw] rounded-none border-2 border-ink p-0 bg-white flex flex-col max-h-[92vh]"
            >
                <DialogHeader className="p-5 border-b-2 border-ink shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <DialogTitle className="font-display text-2xl font-black tracking-tighter text-ink">
                                Data-Driven Landing Page
                            </DialogTitle>
                            <DialogDescription className="mono-label mt-1">
                                {result?.business_type} · {result?.city} · uses top 5 keywords + competitor gaps
                            </DialogDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                data-testid="ddl-regen"
                                onClick={start}
                                disabled={loading}
                                className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                            >
                                <ArrowClockwise size={13} weight="bold" />
                                Regenerate
                            </button>
                            <button
                                data-testid="ddl-copy-lovable"
                                onClick={handleCopyLovable}
                                disabled={!data}
                                className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-40"
                                title="Copy a labeled plain-text block for pasting into Lovable / Framer / etc."
                            >
                                <Copy size={13} weight="bold" />
                                Copy for Lovable
                            </button>
                        </div>
                    </div>
                </DialogHeader>

                <div className="flex-1 min-h-0 overflow-y-auto bg-neutral-50">
                    {loading && <LoadingState />}
                    {!loading && error && (
                        <div className="flex flex-col items-center justify-center gap-3 py-24 px-8">
                            <p className="font-mono text-sm text-red-600 text-center max-w-md">{error}</p>
                            <button
                                onClick={start}
                                className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <ArrowClockwise size={13} weight="bold" />
                                Try Again
                            </button>
                        </div>
                    )}
                    {!loading && !error && data && (
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
                            {/* Live mobile-optimized preview with hero-as-background */}
                            <div
                                data-testid="ddl-preview"
                                className="min-h-[500px] flex items-center justify-center p-8 bg-neutral-100"
                            >
                                <div className="w-full max-w-[420px] shadow-2xl ring-1 ring-black/10 rounded-3xl overflow-hidden bg-white">
                                    {/* Hero: image as full-bleed background with dark overlay + copy on top */}
                                    <div
                                        className="relative min-h-[540px] flex flex-col justify-end p-6"
                                        style={{
                                            backgroundImage: heroSrc
                                                ? `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.75) 100%), url(${heroSrc})`
                                                : "linear-gradient(180deg, #0018A8 0%, #111 100%)",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                        }}
                                    >
                                        <div className="text-white">
                                            <h1
                                                data-testid="ddl-headline"
                                                className="text-3xl font-black leading-tight tracking-tight"
                                            >
                                                {c.headline}
                                            </h1>
                                            <ul className="mt-4 space-y-1.5">
                                                {(c.sub_headlines || []).map((s, i) => (
                                                    <li
                                                        key={i}
                                                        data-testid={`ddl-sub-${i}`}
                                                        className="text-sm text-white/90 flex items-start gap-2"
                                                    >
                                                        <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-white shrink-0" />
                                                        <span>{s}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button
                                                data-testid="ddl-cta"
                                                className="mt-5 w-full py-3 px-4 bg-white text-ink font-bold text-base rounded-lg shadow-lg"
                                            >
                                                {c.cta_text || "Get Started"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="p-6 space-y-4 bg-white">
                                        {(c.body_paragraphs || []).map((p, i) => (
                                            <p
                                                key={i}
                                                data-testid={`ddl-body-${i}`}
                                                className="text-sm leading-relaxed text-neutral-800"
                                            >
                                                {p}
                                            </p>
                                        ))}
                                    </div>

                                    {/* Footer CTA */}
                                    <div className="px-6 pb-6 pt-2 bg-neutral-50 border-t border-neutral-200 text-center">
                                        <button className="w-full py-3 px-4 bg-ink text-white font-bold text-sm rounded-lg">
                                            {c.cta_text || "Get Started"}
                                        </button>
                                        <p className="mt-2 mono-label text-neutral-500">
                                            {data.city} · {data.country_label}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Research signals sidebar */}
                            <aside className="p-5 border-t-2 lg:border-t-0 lg:border-l-2 border-ink bg-white space-y-4">
                                <div>
                                    <div className="mono-label mb-1.5 flex items-center gap-1.5">
                                        <Sparkle size={11} weight="bold" />
                                        Top 5 High-Priority Keywords
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {(data.priority_keywords || []).map((k) => (
                                            <span
                                                key={k}
                                                data-testid="ddl-priority-kw"
                                                className="border-2 border-klein bg-klein/5 text-klein px-1.5 py-0.5 font-mono text-[11px] font-bold"
                                            >
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {data.gap_signals?.length > 0 && (
                                    <div>
                                        <div className="mono-label mb-1.5 flex items-center gap-1.5">
                                            <ArrowSquareOut size={11} weight="bold" />
                                            Competitor Gaps ({data.gap_signals.length})
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {data.gap_signals.slice(0, 12).map((g, i) => (
                                                <span
                                                    key={i}
                                                    className="border border-neutral-300 bg-white text-ink px-1.5 py-0.5 font-mono text-[11px]"
                                                    title={g}
                                                >
                                                    {g.length > 32 ? g.slice(0, 32) + "…" : g}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="border-t border-neutral-200 pt-3">
                                    <div className="mono-label mb-1">Headline length</div>
                                    <div className="font-heading text-2xl font-black text-ink">
                                        {c.headline?.length || 0}
                                        <span className="text-sm text-neutral-500"> / 60</span>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
